import { errorsToDiagnostics } from "./errors-to-diagnostics";
import { mergeCompatibleOverlappingDiagnostics } from "./merge-overlapping-diagnostics";
const lintWorker = new Worker(
    /* webpackChunkName: "latex-linter-worker" */
    new URL("./latex-linter.worker.js", import.meta.url),
);
class Deferred {
    constructor() {
        this.promise = new Promise((resolve, reject) => {
            this.resolve = resolve;
            this.reject = reject;
        });
    }
}
let linterPromise = null; // promise which will resolve to results of the current linting
let queuedRequest = null; // deferred promise for incoming linting requests while the current one is running
let currentView = null;
let currentResolver = null;
let currentRejector = null;
const runLinter = () => {
    lintWorker.postMessage({ text: currentView.state.doc.toString() });
    return new Promise((resolve, reject) => {
        currentResolver = resolve;
        currentRejector = reject;
    });
};
lintWorker.addEventListener("message", (event) => {
    if (event.data && event.data.errors && currentView) {
        const errors = event.data.errors;
        const editorState = currentView.state;
        const doc = editorState.doc;
        const cursorPosition = editorState.selection.main.head;
        const diagnostics = errorsToDiagnostics(errors, cursorPosition, doc.length);
        const mergedDiagnostics = mergeCompatibleOverlappingDiagnostics(diagnostics);
        currentResolver(mergedDiagnostics);
        // make compile controller aware of lint errors via editor:lint event
        const lintErrors = errors.filter((e) => e.type !== "info");
        const firstLintError = lintErrors[0] || null;
        const hasLintingError = lintErrors.length > 0;
        window.dispatchEvent(
            new CustomEvent("editor:lint", {
                detail: {
                    hasLintingError,
                    firstError: firstLintError
                        ? {
                              row: Number.isFinite(firstLintError.start_row)
                                  ? firstLintError.start_row
                                  : firstLintError.row,
                              column: Number.isFinite(firstLintError.start_col)
                                  ? firstLintError.start_col
                                  : firstLintError.column,
                              endRow: Number.isFinite(firstLintError.end_row)
                                  ? firstLintError.end_row
                                  : firstLintError.row,
                              endColumn: Number.isFinite(firstLintError.end_col)
                                  ? firstLintError.end_col
                                  : firstLintError.column,
                              message: firstLintError.text || "",
                          }
                        : null,
                },
            }),
        );
        currentResolver = null;
        currentRejector = null;
    }
});
lintWorker.addEventListener("error", (event) => {
    console.error("latex linter worker failed", event);
    if (currentRejector) {
        currentRejector(event.error || new Error("latex linter worker failed"));
        currentRejector = null;
    }
    if (currentResolver) {
        currentResolver([]);
        currentResolver = null;
    }
    queuedRequest = null;
    linterPromise = null;
});
const executeQueuedAction = (deferred) => {
    runLinter()
        .then((result) => deferred.resolve(result))
        .catch((error) => {
            console.error("latex linter run failed", error);
            deferred.resolve([]);
        });
    return deferred.promise;
};
const processQueue = () => {
    if (queuedRequest) {
        linterPromise = executeQueuedAction(queuedRequest).then(processQueue);
        queuedRequest = null;
    } else {
        linterPromise = null;
    }
};
export const latexLinter = (view) => {
    // always update the view, we use it to filter the results to the current buffer
    currentView = view;
    // if a linting request isn't already running, start it running
    if (!linterPromise) {
        linterPromise = runLinter();
        linterPromise
            .catch((error) => {
                console.error("latex linter run failed", error);
                return [];
            })
            .then(processQueue);
        return linterPromise;
    } else {
        // otherwise create a single deferred promise which we will return to all subsequent requests
        if (!queuedRequest) {
            queuedRequest = new Deferred();
        }
        return queuedRequest.promise;
    }
};
